import React from "react";
import { useCart } from "~/context/CartContext";
import { IconX, IconMinus, IconPlus, IconTrash } from "@tabler/icons-react";
import { Link } from "@remix-run/react";

interface LineProps {
  node: {
    id: string;
    merchandise: {
      image: {
        url: string;
      };
      product: {
        title: string;
      };
      selectedOptions: {
        value: string;
      }[];
    };
    cost: {
      amountPerQuantity: {
        amount: number;
      };
    };
    quantity: number;
  };
}


const SliderCart: React.FC = () => {
  const { cart, cartOpen, setCartOpen, updateQuantity } = useCart();
  return (
    <>
      <div
        id="shoppingCartDrawer"
        className={`antialiased fixed right-0 top-20 -translate-y-px z-40 shadow-lg flex h-[calc(100vh-79px)] w-full md:max-w-sm ${
          cartOpen ? "translate-x-0" : "translate-x-full"
        } flex-col justify-between space-y-4 overflow-y-auto bg-white shadow-md p-4 transition-transform`}
        tabIndex={-1}
        aria-labelledby="drawer-label"
        aria-hidden="true"
      >
        <div className="flex-1 space-y-4 flex flex-col">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold uppercase leading-none text-gray-500">
              Shopping cart
            </h3>

            <button
              type="button"
              onClick={() => setCartOpen(false)}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            >
              <IconX className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 divide-y divide-gray-200 border-t border-gray-200">
            {cart?.lines?.edges.map((line: LineProps, i: number) => (
              <div className="flex items-start gap-4 py-4" key={i}>
                <a href="/" className="h-20 w-20 shrink-0">
                  <img
                    className="h-full w-full"
                    src={line.node.merchandise.image.url}
                    alt="product"
                  />
                </a>

                <div className="min-w-0 flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                      <a
                        href="/"
                        className="text-base font-medium text-gray-900 hover:underline leading-[1.1]"
                      >
                        {line.node.merchandise.product.title}
                      </a>
                      <span className="text-xs text-gray-500">
                        {line.node.merchandise.selectedOptions
                          .map((sO: {value: string}) => sO.value)
                          .join(", ")}
                      </span>
                    </div>
                    <p className="text-base font-bold text-gray-900">
                      $
                      {line.node.cost.amountPerQuantity.amount *
                        line.node.quantity}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500"></p>
                  <div className="flex justify-between items-center gap-4">
                    <form>
                      <label htmlFor="quantity-input" className="sr-only">
                        Choose quantity:
                      </label>
                      <div className="relative flex w-32 items-center">
                        <button
                          type="button"
                          id="decrement-button"
                          onClick={() =>
                            updateQuantity(
                              line.node.id,
                              Math.max(line.node.quantity - 1)
                            )
                          }
                          className="flex h-9 items-center justify-center rounded-s-lg border border-gray-300 bg-gray-100 p-3 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100"
                        >
                          <IconMinus className="h-3 w-3 text-gray-900" />
                        </button>
                        <input
                          type="text"
                          id="quantity-input"
                          data-input-counter
                          aria-describedby="helper-text-explanation"
                          className="block h-9 w-full border-x-0 border-gray-300 bg-gray-50 py-2 text-center text-sm text-gray-900 focus:border-gray-500 focus:ring-gray-500"
                          defaultValue={line.node.quantity}
                          required
                        />
                        <button
                          type="button"
                          id="increment-button"
                          onClick={() =>
                            updateQuantity(
                              line.node.id,
                              Math.min(line.node.quantity + 1, 10)
                            )
                          }
                          className="flex h-9 items-center justify-center rounded-e-lg border border-gray-300 bg-gray-100 p-3 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100"
                        >
                          <IconPlus className="h-3 w-3 text-gray-900" />
                        </button>
                      </div>
                    </form>

                    <div className="flex items-center justify-end gap-4">
                      <button
                        type="button"
                        onClick={() => updateQuantity(line.node.id, 0)}
                        className="inline-flex items-center text-sm font-medium text-red-600 hover:underline"
                      >
                        <IconTrash className="h-4 w-4 mr-1" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {(cart == null || cart.totalQuantity == 0) && <p className="py-4 text-center">No items in cart</p>}
          </div>

          <div className="mb-4 rounded-lg border border-gray-100 bg-gray-50 px-4">
            <div className="divide-y divide-gray-200">
              <dl className="flex items-center justify-between gap-4 py-3">
                <dt className="font-normal text-gray-500">Subtotal</dt>
                <dd className="font-medium text-gray-900">${Math.floor(cart?.cost?.subtotalAmount?.amount) || 0}</dd>
              </dl>

              <dl className="flex items-center justify-between gap-4 py-3">
                <dt className="font-normal text-gray-500">Shipping</dt>
                <dd className="font-medium text-gray-900">$0</dd>
              </dl>

              <dl className="flex items-center justify-between gap-4 pb-4 pt-3">
                <dt className="font-normal text-gray-500">Tax</dt>
                <dd className="font-medium text-gray-900">$0</dd>
              </dl>

              <dl className="flex items-center justify-between gap-4 py-3 text-lg font-bold text-gray-900">
                <dt>Total</dt>
                <dd>${Math.floor(cart?.cost?.totalAmount?.amount) || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="mt-6 gap-4 sm:flex sm:items-center sm:justify-center">
          <Link
            to={cart?.checkoutUrl}
            type="button"
            className="mb-4 flex w-full items-center justify-center rounded-lg bg-black px-5 py-3 font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-black uppercase sm:mb-0 sm:mt-0"
          >
            Checkout
          </Link>
        </div>
      </div>
    </>
  );
};

export default SliderCart;
